import * as React from "react";
import {
  DataGrid,
  GridRowsProp,
  GridColDef,
  GridSortModel,
  GridToolbar,
  GridFilterModel,
} from "@mui/x-data-grid";
import { IArticle } from "./IArticle";
import { useEffect, useState } from "react";
import {
  Box,
  IconButton,
  LinearProgress,
  Typography,
  createTheme,
  useMediaQuery,
} from "@mui/material";
import ArticleStore from "./Article-store";
import InsertLinkIcon from "@mui/icons-material/InsertLink";
import Link from "@mui/material/Link";
import SideNavigation from "./sideNav";

const columns: GridColDef[] = [
  {
    field: "staffMember",
    width: 200,
    type: "string",
    renderHeader: () => <strong>{"Staff Member"}</strong>,
  },
  {
    field: "articleName",
    width: 850,
    type: "string",
    renderHeader: () => <strong>{"Research Article Name"}</strong>,
  },
  {
    field: "articleURL",
    width: 100,
    type: "string",
    renderHeader: () => <strong>{"Article URL"}</strong>,
    renderCell: (param) => (
      <Link target="_blank" href={`${param.value}`}>
        <IconButton>
          <InsertLinkIcon />
        </IconButton>
      </Link>
    ),
  },
  {
    field: "year",
    width: 100,
    type: "string",
    renderHeader: () => <strong>{"Year"}</strong>,
  },
  {
    field: "citations",
    width: 100,
    type: "string",
    renderHeader: () => <strong>{"Citations"}</strong>,
  },
];

function initializeData(articles: IArticle[] | undefined | null): any[] {
  const rows: any[] = [];

  if (!Array.isArray(articles)) {
    console.error("Invalid articles input:", articles);
    return rows;
  }

  articles.forEach((article, index) => {
    rows.push({
      id: index + 1,
      staffMember: article.staffMember,
      articleName: article.articleName,
      articleURL: article.articleURL,
      year: article.year,
      citations: article.citations,
    });
  });

  return rows;
}

export default function Articles() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterModel, setFilterModel] = useState<GridFilterModel | undefined>(undefined);
  const theme = createTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  async function getArticles() {
    const data = await ArticleStore.getArticles();
    setArticles(data);
  }

  useEffect(() => {
    getArticles();

    const interval = setInterval(() => {
      getArticles();
    }, 500);

    const timer = setTimeout(() => {
      setLoading(false);
    }, 10000); // 10 seconds

    return () => {
      clearInterval(interval);
      clearTimeout(timer);
    };
  }, []);

  const rows: GridRowsProp = initializeData(articles);

  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      field: "year",
      sort: "desc",
    },
  ]);

  const handleSelect = (value: string) => {
    const newFilterModel: GridFilterModel = {
      items: [
        {
          id: 1,
          field: "staffMember",
          operator: "contains",
          value: value,
        },
      ],
    };
    setFilterModel(newFilterModel);
  };

  // ------------------------------
  // Conditional rendering logic
  // ------------------------------
  if (loading) {
    return (
      <Box
        style={{
          width: "80%",
          height: "50vh",
          marginTop: "20vh",
          marginLeft: "10%",
        }}
      >
        <LinearProgress color="secondary" />
      </Box>
    );
  }

  if (!loading && rows.length === 0) {
    return (
      <Box
        sx={{
          width: "100%",
          height: "80vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
        }}
      >
        <Typography variant="h6" color="error">
          Sorry, we were unable to fetch the articles.
        </Typography>
        <Typography variant="body1" sx={{ mt: 1 }}>
          Please try again later.
        </Typography>
      </Box>
    );
  }

  // Main table
  return (
    <Box style={{ width: "100%", minHeight: "80vh" }}>
      <SideNavigation isMobile={isMobile} onSelect={handleSelect} />
      <DataGrid
        initialState={{
          pagination: { paginationModel: { pageSize: 20 } },
          filter: {
            filterModel: filterModel,
          },
        }}
        pageSizeOptions={[20, 50, 100]}
        slots={{
          toolbar: GridToolbar,
        }}
        autoHeight
        scrollbarSize={20}
        rows={rows}
        columns={columns}
        sortModel={sortModel}
        onSortModelChange={(newSortModel) => setSortModel(newSortModel)}
        filterModel={filterModel}
        onFilterModelChange={(newFilterModel) =>
          setFilterModel(newFilterModel)
        }
      />
    </Box>
  );
}
